import { createContext, useContext } from 'react';
import { SceneState } from '../types/heygen';
import { CardOption } from '../components/shared/card-selector';
import { ProjectScene } from '../../../types/avatar-video';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface CustomAvatar extends CardOption {
  groupId: string;
}

export interface EditorContextValue {
  // Form
  form: { title: string; scenes: SceneState[] };
  title: string;
  onTitleChange: (value: string) => void;

  // Active scene
  scene: SceneState;
  activeIndex: number;
  totalScenes: number;
  setActiveIndex: (index: number) => void;

  // Scene actions
  updateScene: (index: number, key: keyof SceneState, value: string) => void;
  setSceneOnlyVoice: (index: number, onlyVoice: boolean) => void;
  addScene: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
  onReset: () => void;

  // Assets
  avatars: CustomAvatar[];
  onAvatarUploaded: (option: CustomAvatar) => void;
  backgrounds: CardOption[];
  onBackgroundUploaded: (option: CardOption) => void;

  // Project generation
  projectId: string | null;
  projectStatus: 'idle' | 'processing' | 'completed' | 'partial' | 'failed';
  projectScenes: ProjectScene[];
  projectTotalScenes: number;
  projectCompletedScenes: number;
  isProcessing: boolean;
  isValid: boolean;
  onGenerate: () => void;
  onUpdate: () => void;
  onRegenerate: (projectId: string) => void | Promise<void>;
  loadProjectForEdit: (projectId: string) => void | Promise<void>;
  editingProjectId: string | null; // 'null' = create mode

  // Resolution
  selectedResolution: string;
  setSelectedResolution: (id: string) => void;

  // Placa de cierre (outro). `null` = "Sin placa" (default).
  selectedPlacaId: string | null;
  setSelectedPlacaId: (id: string | null) => void;

  // Track de música de fondo. `null` = "Sin música" (default).
  selectedMusicTrackId: string | null;
  setSelectedMusicTrackId: (id: string | null) => void;

  // Subtitles toggle. Default is true.
  enableSubtitles: boolean;
  setEnableSubtitles: (enabled: boolean) => void;

  // UI state
  historyOpen: boolean;
  setHistoryOpen: (open: boolean) => void;
  scenePanelCollapsed: boolean;
  toggleScenePanel: () => void;
  progressModalOpen: boolean;
  closeProgressModal: () => void;
}

// ─── Context ─────────────────────────────────────────────────────────────────

export const EditorContext = createContext<EditorContextValue | null>(null);

export const useEditorContext = (): EditorContextValue => {
  const ctx = useContext(EditorContext);
  if (!ctx) throw new Error('useEditorContext must be used within EditorProvider');
  return ctx;
};
